/* AuditPolicyEditor.js */

"use strict";
const GameEditor = require("@gluenetworks/game_editor");
const Steve = require("@gluenetworks/steve_core").getInstance();
const _ = require("lodash");

/**
 * @param {UserSession} userSession
 * @param {ConfigAuditPolicyEntity} ConfigAuditPolicy
 * @param {SolutionsManager} solutionsManager
 * @param {IO} steveIo
 */

class AuditPolicyEditor extends GameEditor {
    constructor(
        logger,
        di,
        registeredDetails,
        editorState,
        editorLayoutMgr,
        editorLayout,
        DeviceEntity,
        ConfigAuditPolicy,
        userSession,
        editorTabCmpnt,
        solutionsManager,
        steveIo,
        storageFactory,
        LicenseRestriction,
        OsImageServerEntity,
        ConstructTypeEntity,
        VersionControlConfigDef
    ) {
        super(logger, di, editorState.editorState, editorLayoutMgr);
        this._logger = logger;
        this._registeredDetails = registeredDetails;
        this._editorLayout = editorLayout;
        this._DeviceEntity = DeviceEntity;
        this._ConfigAuditPolicyEntity = ConfigAuditPolicy;
        this._tabCmpnt = editorTabCmpnt;
        this._editorTabState = editorState;
        this._steveIo = steveIo;
        this._storageFactory = storageFactory;
        this._licenseRestriction = LicenseRestriction;
        this._osImageServerEntity = OsImageServerEntity;
        this._constructTypeEntityDef = ConstructTypeEntity;
        this._rsiIds = [];
        this._rsiIdsMap = {};
        this._versionControlConfigEntityDef = VersionControlConfigDef;
        /* User Data - Start */
        this._userDataStorage = this._storageFactory.getRemoteStorageInstance(
            "views",
            "test_rule_veil"
        );
        /* User Data - End */

        this._userSession = userSession;
        this._activeOrgId = userSession.activeOrganization.id;

        this.currentEntityRemoved = false;
        this.currentEntityDeleteInProgress = false;

        this.isOSMSolutionAvailable = false;

        let solutionDefinition = solutionsManager.getActiveSolution();
        this._auditPolicyEditorViews = _.get(
            solutionDefinition,
            "auditPolicyEditor.viewNamespaces",
            []
        );
        this._auditPolicyEditorActions = _.get(
            solutionDefinition,
            "auditPolicyEditor.actionNamespaces",
            []
        );
        let launchOptions = editorState.launchOptions;
        this._versionsViewNS =
            "App.GameConsole.Section.GameCenter.AuditPolicyEditor.Views.Versions";

        this.setNamespaceGlobs(
            this._auditPolicyEditorViews.concat(this._auditPolicyEditorActions),
            true
        );
        this._isAuthorizedForVersionControl =
            (this._userSession.authorizedActions || []).indexOf("versionControlAccess") >= 0;

        this._auditPolicy = undefined;
        this._conceptItems = {};
        this._auditPolicyId = launchOptions.policyId;
        this._auditPolicyName = launchOptions.title || "Unknown";
        this._os = {};

        this.retardActions(true);

        this.whenPolicyIsReady = _.memoize(this.whenPolicyIsReady);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListenersOnPolicy() {
        this._auditPolicy.on(
            "NamePropertyUpdated",
            (this._onNamePropertyUpdated = this._onNamePropertyUpdated.bind(this))
        );
        this._auditPolicy.on(
            "LastCommitHashPropertyUpdated",
            (this._onLastCommitHashPropertyUpdated =
                this._onLastCommitHashPropertyUpdated.bind(this))
        );
        this._auditPolicy.on(
            "EntityDeletedFromSystem",
            (this._onAuditPolicyRemoved = this._onAuditPolicyRemoved.bind(this))
        );
        this._auditPolicy.on(
            "DirtyStateChanged",
            (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this))
        );
    }

    _unregisterListenersOnPolicy() {
        this._auditPolicy.removeListener("NamePropertyUpdated", this._onNamePropertyUpdated);
        this._auditPolicy.removeListener("EntityDeletedFromSystem", this._onAuditPolicyRemoved);
        this._auditPolicy.removeListener("DirtyStateChanged", this._onDirtyStateChanged);
        this._auditPolicy.removeListener(
            "LastCommitHashPropertyUpdated",
            this._onLastCommitHashPropertyUpdated
        );
    }

    _onDirtyStateChanged(newVal) {
        this._tabCmpnt.setDirty(newVal);
    }

    _onNamePropertyUpdated() {
        this.updateTab(this._auditPolicy.pristineProperties.name);
    }

    _onLastCommitHashPropertyUpdated() {
        this.emit("LastCommitHashPropertyUpdated");
    }

    _onAuditPolicyRemoved() {
        this.currentEntityRemoved = true;
        if (!this.currentEntityDeleteInProgress) {
            this.currentEntityRemoved = false;
            this.closeEditor(this.editorTabStateId, true);
        }
    }

    getTabCmpnt() {
        return this._tabCmpnt;
    }

    getAuditPolicy() {
        return this._auditPolicy;
    }

    get activeOrgId() {
        return this._activeOrgId;
    }

    get configAuditPolicyEntity() {
        return this._ConfigAuditPolicyEntity;
    }

    set conceptItems(newConceptItems) {
        this._conceptItems = _.cloneDeep(newConceptItems);
        return this;
    }

    get conceptItems() {
        return this._conceptItems;
    }

    set os(newOs) {
        this._os = _.cloneDeep(newOs);
        return this;
    }

    get os() {
        return this._os;
    }

    get steveIO() {
        return this._steveIo;
    }

    get editorTabStateId() {
        return this._editorTabState.id;
    }

    whenPolicyIsReady() {
        return Promise.resolve()
            .then(() => this._licenseRestriction.whenLicenseFeedIsReady())
            .then(() => {
                this.isOSMSolutionAvailable = this._licenseRestriction.checkLicense("OSM");
                if (this.isOSMSolutionAvailable === true) {
                    return this._osImageServerEntity.loadAscendOsImageServers(
                        this._userSession.activeOrganization.id,
                        {},
                        {
                            id: true,
                            name: true,
                            identifier: true,
                            isMain: true,
                        }
                    );
                }
                return Promise.resolve();
            })
            .then((osImageServers) => {
                this.osImageServerList = osImageServers;
                return Promise.resolve();
            })
            .then(() => {
                return this._ConfigAuditPolicyEntity.retrieve(
                    this.activeOrgId,
                    "ConfigAuditPolicy",
                    this._auditPolicyId
                );
            })
            .then((auditPolicy) => {
                this._auditPolicy = auditPolicy;
                this._registerListenersOnPolicy();
                return auditPolicy;
            })
            .then(() => {
                if (!Steve.controlAppNav._versionControlLocalEnabled) {
                    this.hideBarView(this._versionsViewNS);
                }
                return;
            });
    }

    whenConceptItemsAreReady() {
        return this._ConfigAuditPolicyEntity
            .loadConceptItemLabelValues(this.activeOrgId)
            .then((conceptItems) => {
                this.conceptItems = conceptItems.reduce((result, item) => {
                    result[item.value] = item;
                    return result;
                }, {});
                this.os = this._DeviceEntity.osMap;
                return Promise.resolve();
            })
            .then(() => {
                return this._ConfigAuditPolicyEntity.loadRsiLabelValues(this.activeOrgId, {
                    "Config Modeling Only": {$ne: true},
                });
            })
            .then((rsiIds) => {
                this._rsiIds = rsiIds;
                this._rsiIdsMap = this._rsiIds.reduce((acc, item) => {
                    Object.assign(acc, {[item.value]: item});
                    return acc;
                }, {});
                return Promise.resolve();
            });
    }

    _loadVersionControlEntity() {
        if (!this._isAuthorizedForVersionControl) {
            return Promise.reject("User is not authorized for version control");
        }
        return this._versionControlConfigEntityDef
            .loadVersionControlConfigs(this.activeOrgId)
            .then((versionControlConfigEntityArr) => {
                if (versionControlConfigEntityArr.length > 0) {
                    this._versionControlConfigEntity = versionControlConfigEntityArr[0];
                }
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    updateTab(name) {
        this._tabCmpnt.setTitle(name);
    }

    updateTabIcon(iconName) {
        this._tabCmpnt.setEditorIconName(iconName);
    }

    closeEditor(id, force) {
        id = id || this._editorTabState.id;
        return super.closeEditor(id, force);
    }

    shutdown() {
        let logger = this._logger;
        this.whenPolicyIsReady.cache.clear();

        return super
            .shutdown()
            .then(() => {
                if (!_.isUndefined(this._auditPolicy)) {
                    this._unregisterListenersOnPolicy();
                    return this._auditPolicy.shutdown();
                } else {
                    return Promise.resolve();
                }
            })
            .then(() => {
                this._logger = undefined;
                this._editorTabState = undefined;
                this._steveIo = undefined;

                this._tabCmpnt = undefined;
                this._auditPolicy = undefined;
                this._registeredDetails = undefined;
                this._editorLayout = undefined;
                this._activeOrgId = undefined;
                this._userSession = undefined;
                this._auditPolicyId = undefined;
                this._auditPolicyName = undefined;
                this._conceptItems = undefined;
                this._storageFactory = undefined;
                this._userDataStorage = undefined;
                this._os = undefined;
                this.currentEntityRemoved = undefined;
                this.isOSMSolutionAvailable = undefined;
                this.currentEntityDeleteInProgress = undefined;
                this._rsiIds = undefined;
                this._rsiIdsMap = undefined;
                this._versionControlConfigEntity = undefined;
                this._versionControlConfigEntityDef = undefined;
                this._isAuthorizedForVersionControl = undefined;
            })
            .catch((err) => {
                logger.warn({error: err}, "AuditPolicyEditor.shutdown() error");
            });
    }

    /*******************************************/
    /* END PUBLIC COMPONENT MODEL METHODS      */
    /* START PUBLIC API METHODS                */
    /*******************************************/
    getExecutionReport(executionEntity) {
        return executionEntity.generateWorkReports().then((reports) => {
            if (_.isEmpty(reports)) {
                throw new Error("Cannot retrieve the selected workEntity report");
            }
            const report = Object.values(reports)[0];
            const executionTitle = _.get(executionEntity.getPristineProperties(), "title", "");
            const fileName = `DeviceAudit-Execution-Report - ${executionTitle}.csv`;
            // TODO Quick fix to be refactored in the BE
            return {fileName, content: report.contents.replace(/,Expected,/gim, ",Required,")};
        });
    }

    saveJsonFile(fileName, content) {
        this._steveIo.fileFactory.saveJsonAsFile(content, fileName);
        return true;
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = AuditPolicyEditor;

AuditPolicyEditor.$inject = [
    "$teve.logger",
    "$teve.di",
    "$teve.registeredDetails",
    "editorLayout.editorState",
    "editorLayout.editorLayoutMgr",
    "EditorLayout.EditorLayout",
    "$teve.entities.Device",
    "$teve.entities.ConfigAuditPolicy",
    "$teve.userSession",
    "editorLayout.editor.tabComponent",
    "$teve.solutionsManager",
    "$teve.io",
    "$teve.storageFactory",
    "$teve.licenseRestriction",
    "$teve.entities.OsImageServer",
    "$teve.entities.ConstructType",
    "$teve.entities.VersionControlConfig",
];

//Optional convenient way to set a tab icon.
AuditPolicyEditor.$editorIcon = "configdrift.audit_policy";
AuditPolicyEditor.$editorIconType = "base-icon-gl";
